export type RealtimeEventHandler<T = unknown> = (event: T) => void

import { defaultCallSessionMediaPreferences, useCallStore } from '@/store/callStore'
import { usePresenceStore } from '@/store/presenceStore'
import type { CallStatus, OnlineStatus, VideoCall } from '@/types'
import { callApi } from './callApi'
import { getAvatarUrl } from './image'
import { getEnvConfig } from './runtime-env'
import { shengwangClient } from './shengwangClient'
import { releaseEndedOrder, retryPendingEndedOrderReleases } from './anchorOrderRelease'
import { clearOutgoingCallTimeout, releaseOutgoingCall } from './outgoingCallLifecycle'
import { reportAnchorActivity } from './anchorActivityLogger'
import { rtcJoinMetadata } from './rtcJoinDiagnostics'
import { message } from 'antd'
import { useAuthStore } from '@/store/authStore'
import { canAnchorHandleIncomingCall } from './anchorEligibility'
import { clearAllIncomingAutoAccepts, clearIncomingAutoAccept, scheduleIncomingAutoAccept } from './incomingAutoAccept'
import {
  clearCallOrderConnecting,
  isCallOrderConnecting,
  isCallOrderEnded,
  markCallOrderConnecting,
  markCallOrderEnded,
  resetConnectingCallOrders,
  resetEndedCallOrders,
  resetCallOrderLifecycleGuard,
} from './callOrderLifecycleGuard'

interface RealtimeConnectOptions {
  url: string
  token: string
}

type RealtimeMessage = Record<string, unknown>
type CallDirection = NonNullable<VideoCall['direction']>

export interface RealtimeClient {
  connect: (options: RealtimeConnectOptions) => void
  disconnect: () => void
  subscribe: <T = unknown>(channel: string, handler: RealtimeEventHandler<T>) => () => void
  publish: (channel: string, payload: unknown) => void
  isConnected: () => boolean
}

const RECONNECT_DELAYS_MS = [1000, 2000, 4000, 5000]
const CLIENT_NAME = 'anchor-h5'
const CLIENT_VERSION = '1.0.0'
const VALID_CALL_STATUSES = new Set<CallStatus>(['idle', 'ringing', 'busy'])
const VALID_ONLINE_STATUSES = new Set<OnlineStatus>(['online', 'offline', 'busy'])
const VIDEO_CALL_MESSAGE_TYPE = 'video_call'
const PRESENCE_ACK_EVENT = 'presence_ack'
const CALL_CANCELED_EVENT = 'call_canceled'
const CALL_CLOSED_EVENT = 'call_closed'
const CALL_EXPIRED_EVENT = 'call_expired'
const REALTIME_EVENT_TYPES = new Set([
  PRESENCE_ACK_EVENT,
  'incoming_call',
  'call_ready',
  'call_rejected',
  CALL_CANCELED_EVENT,
  CALL_CLOSED_EVENT,
  'call_timeout',
  'call_ended',
  'call_failed',
  CALL_EXPIRED_EVENT,
])
const recordedIncomingCalls = new Set<string>()
const endingCallOrders = new Set<string>()
const incomingCallTimeoutTimers = new Map<string, ReturnType<typeof setTimeout>>()
const INCOMING_CALL_TIMEOUT_BUFFER_MS = 1000

function getChangedOutgoingSessionMediaPreferences(orderID: string) {
  const preferences = useCallStore.getState().sessionMediaPreferences
  if (!preferences || preferences.orderID !== orderID) {
    return undefined
  }
  const hasChanged =
    preferences.localAudioEnabled !== defaultCallSessionMediaPreferences.localAudioEnabled ||
    preferences.localVideoEnabled !== defaultCallSessionMediaPreferences.localVideoEnabled ||
    preferences.remoteAudioEnabled !== defaultCallSessionMediaPreferences.remoteAudioEnabled
  if (!hasChanged) {
    return undefined
  }
  return {
    localAudioEnabled: preferences.localAudioEnabled,
    localVideoEnabled: preferences.localVideoEnabled,
    remoteAudioEnabled: preferences.remoteAudioEnabled,
  }
}

export function buildRealtimeConnectCommand(id: number, token: string): RealtimeMessage {
  return {
    id,
    connect: {
      data: {
        token,
      },
      name: CLIENT_NAME,
      version: CLIENT_VERSION,
    },
  }
}

export function buildRealtimeOperationCommand(id: number, operation: string, payload: unknown): RealtimeMessage {
  return {
    id,
    [operation]: asRecord(payload) || {},
  }
}

export function isRealtimeJSONPing(line: string) {
  try {
    const message = JSON.parse(line)
    return Boolean(message && typeof message === 'object' && !Array.isArray(message) && Object.keys(message as RealtimeMessage).length === 0)
  } catch {
    return false
  }
}

function asRecord(value: unknown): RealtimeMessage | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as RealtimeMessage) : null
}

function parseData(value: unknown): RealtimeMessage | null {
  if (typeof value === 'string') {
    try {
      return asRecord(JSON.parse(value))
    } catch {
      return null
    }
  }
  return asRecord(value)
}

function extractEventPayload(message: RealtimeMessage): RealtimeMessage | null {
  const push = asRecord(message.push)
  const pub = asRecord(push?.pub)
  const pubData = parseData(pub?.data)
  if (pubData) {
    return pubData
  }

  const result = asRecord(message.result)
  const resultData = parseData(result?.data)
  if (resultData) {
    return resultData
  }

  if (
    typeof message.type === 'string' ||
    typeof message.event_type === 'string' ||
    typeof message.event === 'string' ||
    typeof message.action === 'string' ||
    message.message_type === VIDEO_CALL_MESSAGE_TYPE
  ) {
    return message
  }

  return null
}

function unwrapVideoCallPayload(message: RealtimeMessage): RealtimeMessage {
  if (message.message_type !== VIDEO_CALL_MESSAGE_TYPE) {
    return message
  }
  const content = parseData(message.content)
  return content ? { ...content, message_type: VIDEO_CALL_MESSAGE_TYPE } : message
}

function getEventType(payload: RealtimeMessage) {
  const eventType = getStringField(payload, 'type') || getStringField(payload, 'event_type') || getStringField(payload, 'event') || getStringField(payload, 'action')
  return REALTIME_EVENT_TYPES.has(eventType) ? eventType : ''
}

function getOrderID(payload: RealtimeMessage) {
  return getStringField(asRecord(payload.order), 'order_id') || getStringField(payload, 'order_id')
}

function getUserID(payload: RealtimeMessage) {
  const userID = typeof payload.user_id === 'string' ? payload.user_id.trim() : ''
  if (userID) {
    return userID
  }
  return typeof payload.from_user_id === 'string' ? payload.from_user_id.trim() : ''
}

function getDirection(payload: RealtimeMessage) {
  return typeof payload.direction === 'string' ? payload.direction.trim() : ''
}

function getStringField(payload: RealtimeMessage | null, key: string) {
  const value = payload?.[key]
  return typeof value === 'string' ? value.trim() : ''
}

function getNumberField(payload: RealtimeMessage | null, key: string) {
  const value = payload?.[key]
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
  }
  return undefined
}

function getTimestampField(payload: RealtimeMessage | null, key: string) {
  const value = payload?.[key]
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value
  }
  if (typeof value === 'string' && value.trim()) {
    const trimmed = value.trim()
    const parsed = Number(trimmed)
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed
    }
    const timestamp = Date.parse(trimmed)
    if (Number.isFinite(timestamp)) {
      return Math.floor(timestamp / 1000)
    }
  }
  return undefined
}

function getBooleanField(payload: RealtimeMessage | null, key: string) {
  const value = payload?.[key]
  if (typeof value === 'boolean') {
    return value
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (normalized === 'true') {
      return true
    }
    if (normalized === 'false') {
      return false
    }
  }
  return undefined
}

function getDisplayAvatar(display: RealtimeMessage | null, payload?: RealtimeMessage) {
  const avatar = getStringField(display, 'avatar') || getStringField(payload || null, 'avatar')
  return getAvatarUrl(avatar, getEnvConfig().CDN_URL)
}

function isAnchorReceiver(payload: RealtimeMessage) {
  const receiverRole = getStringField(payload, 'receiver_role')
  return !receiverRole || receiverRole === 'anchor'
}

function isVideoCallPayload(payload: RealtimeMessage) {
  return getStringField(payload, 'message_type') === VIDEO_CALL_MESSAGE_TYPE
}

function toIncomingVideoCall(payload: RealtimeMessage): VideoCall | null {
  if (!isVideoCallPayload(payload)) {
    return null
  }

  const order = asRecord(payload.order)
  const display = asRecord(payload.display)
  const media = asRecord(payload.media)
  if (!order || !display) {
    return null
  }
  const roomID = getStringField(order, 'room_id')
  const orderID = getStringField(order, 'order_id')
  const userID = getStringField(display, 'id')
  const providerAppID = getStringField(media, 'provider_app_id') || getStringField(media, 'app_id')
  const channelName = getStringField(media, 'channel_name')
  const agoraUID = getNumberField(media, 'agora_uid') ?? getNumberField(media, 'uid')
  const expireAt = getTimestampField(order, 'expire_at') ?? getTimestampField(payload, 'expire_at')

  if (!orderID || !userID) {
    return null
  }

  return {
    room_id: roomID || undefined,
    order_id: orderID,
    order_no: getStringField(order, 'order_no') || undefined,
    order_status: getStringField(order, 'status') || undefined,
    entrypoint: getStringField(order, 'entrypoint') || undefined,
    order_expire_at: getStringField(order, 'expire_at') || undefined,
    expire_at: expireAt,
    user_id: userID,
    from_user_id: userID,
    anchor_id: '',
    direction: 'user_to_anchor',
    receiver_role: 'anchor',
    nickname: getStringField(display, 'nickname') || getStringField(payload, 'nickname') || undefined,
    avatar: getDisplayAvatar(display, payload),
    provider: getStringField(media, 'provider') || undefined,
    provider_app_id: providerAppID || undefined,
    app_id: providerAppID || undefined,
    channel_name: channelName || undefined,
    rtc_role: getStringField(media, 'rtc_role') || undefined,
    agora_uid: agoraUID,
    uid: agoraUID,
    media_token_available: getBooleanField(media, 'media_token_available'),
    status: 'ringing',
  }
}

function toOutgoingReadyCall(payload: RealtimeMessage, expectedOrderID?: string): VideoCall | null {
  const direction = getDirection(payload)
  if (direction !== 'anchor_to_user' && !expectedOrderID) {
    return null
  }

  const order = asRecord(payload.order)
  const display = asRecord(payload.display)
  const media = asRecord(payload.media)
  if (!order || !media) {
    return null
  }
  const orderID = getStringField(order, 'order_id') || getStringField(payload, 'order_id')
  if (expectedOrderID && orderID !== expectedOrderID) {
    return null
  }
  const userID = getStringField(display, 'id') || getUserID(payload)
  const providerAppID = getStringField(media, 'provider_app_id')
  const channelName = getStringField(media, 'channel_name')
  const agoraUID = getNumberField(media, 'agora_uid')
  if (!orderID || !providerAppID || !channelName || agoraUID === undefined) {
    return null
  }

  return {
    order_id: orderID,
    order_no: getStringField(order, 'order_no') || undefined,
    order_status: getStringField(order, 'status') || undefined,
    user_id: userID,
    anchor_id: '',
    direction: 'anchor_to_user',
    receiver_role: getStringField(payload, 'receiver_role') || undefined,
    nickname: getStringField(display, 'nickname') || undefined,
    avatar: getDisplayAvatar(display),
    provider: getStringField(media, 'provider') || undefined,
    provider_app_id: providerAppID,
    app_id: providerAppID,
    channel_name: channelName,
    rtc_role: getStringField(media, 'rtc_role') || undefined,
    agora_uid: agoraUID,
    uid: agoraUID,
    media_token_available: getBooleanField(media, 'media_token_available'),
    rtc_token: getStringField(media, 'rtc_token') || getStringField(media, 'token') || getStringField(payload, 'rtc_token') || undefined,
    expire_at: getNumberField(media, 'expire_at') ?? getNumberField(payload, 'expire_at'),
    status: 'accepted',
  }
}

function toIncomingReadyCall(payload: RealtimeMessage, expectedOrderID?: string): VideoCall | null {
  const direction = getDirection(payload)
  if (!isVideoCallPayload(payload) || (direction !== 'user_to_anchor' && !expectedOrderID)) {
    return null
  }

  const order = asRecord(payload.order)
  const display = asRecord(payload.display)
  const media = asRecord(payload.media)
  if (!order || !media) {
    return null
  }
  const orderID = getStringField(order, 'order_id') || getStringField(payload, 'order_id')
  if (expectedOrderID && orderID !== expectedOrderID) {
    return null
  }
  const userID = getStringField(display, 'id') || getUserID(payload)
  const providerAppID = getStringField(media, 'provider_app_id') || getStringField(media, 'app_id')
  const channelName = getStringField(media, 'channel_name')
  const agoraUID = getNumberField(media, 'agora_uid') ?? getNumberField(media, 'uid')
  const rtcToken = getStringField(media, 'rtc_token') || getStringField(media, 'token') || getStringField(payload, 'rtc_token')
  if (!orderID || !userID || !providerAppID || !channelName || agoraUID === undefined) {
    return null
  }

  return {
    room_id: getStringField(order, 'room_id') || undefined,
    order_id: orderID,
    order_no: getStringField(order, 'order_no') || undefined,
    order_status: getStringField(order, 'status') || undefined,
    entrypoint: getStringField(order, 'entrypoint') || undefined,
    order_expire_at: getStringField(order, 'expire_at') || undefined,
    user_id: userID,
    from_user_id: userID,
    anchor_id: '',
    direction: 'user_to_anchor',
    receiver_role: getStringField(payload, 'receiver_role') || 'anchor',
    nickname: getStringField(display, 'nickname') || undefined,
    avatar: getDisplayAvatar(display, payload),
    provider: getStringField(media, 'provider') || undefined,
    provider_app_id: providerAppID,
    app_id: providerAppID,
    channel_name: channelName,
    rtc_channel: channelName,
    rtc_role: getStringField(media, 'rtc_role') || undefined,
    agora_uid: agoraUID,
    uid: agoraUID,
    media_token_available: getBooleanField(media, 'media_token_available'),
    rtc_token: rtcToken || undefined,
    expire_at: getNumberField(media, 'expire_at') ?? getNumberField(payload, 'expire_at'),
    status: 'accepted',
  }
}

function getCallReadyOrderID(payload: RealtimeMessage) {
  return getStringField(asRecord(payload.order), 'order_id') || getStringField(payload, 'order_id')
}

function getCallReadyMediaAvailable(payload: RealtimeMessage) {
  return getBooleanField(asRecord(payload.media), 'media_token_available')
}

async function connectOutgoingReadyCall(readyCall: VideoCall) {
  const orderID = readyCall.order_id || ''
  if (!orderID || isCallOrderEnded(orderID)) {
    return
  }

  clearOutgoingCallTimeout(orderID)
  let connectedCall: VideoCall | null = null
  try {
    const response = await callApi.connectedCall(orderID)
    if (isCallOrderEnded(orderID)) {
      return
    }
    connectedCall = {
      ...readyCall,
      ...(response.data || null),
      user_id: readyCall.user_id || response.data?.user_id || '',
      order_id: orderID,
      status: 'accepted',
    } as VideoCall
    const latestCalls = useCallStore.getState()
    const outgoingCall = latestCalls.outgoingCall
    if (isCallOrderEnded(orderID) || !outgoingCall || outgoingCall.order_id !== orderID || latestCalls.currentCall) {
      await releaseEndedOrder(orderID, 'call_canceled')
      return
    }
    connectedCall = {
      ...outgoingCall,
      ...readyCall,
      ...(response.data || null),
      anchor_id: outgoingCall.anchor_id,
      user_id: readyCall.user_id || response.data?.user_id || outgoingCall.user_id,
      order_id: orderID,
      direction: outgoingCall.direction || 'anchor_to_user',
      status: 'accepted',
    } as VideoCall
    if (isCallOrderEnded(orderID)) {
      await releaseEndedOrder(orderID, 'user_hangup')
      return
    }
    markCallOrderConnecting(orderID)
    reportAnchorActivity({
      event_type: 'outgoing_call_rtc_join_started',
      event_source: 'anchor_h5_rtc',
      order_id: orderID,
      call_direction: 'anchor_to_user',
      counterparty_user_id: connectedCall.user_id,
      rtc_channel: connectedCall.channel_name || connectedCall.rtc_channel,
      rtc_uid: connectedCall.uid,
      metadata: rtcJoinMetadata(connectedCall, 'join_attempt'),
    })
    const initialMediaPreferences = getChangedOutgoingSessionMediaPreferences(orderID)
    if (initialMediaPreferences) {
      await shengwangClient.enterCall(connectedCall, initialMediaPreferences)
    } else {
      await shengwangClient.enterCall(connectedCall)
    }
    reportAnchorActivity({
      event_type: 'outgoing_call_rtc_joined',
      event_source: 'anchor_h5_rtc',
      order_id: orderID,
      call_direction: 'anchor_to_user',
      counterparty_user_id: connectedCall.user_id,
      rtc_channel: connectedCall.channel_name || connectedCall.rtc_channel,
      rtc_uid: connectedCall.uid,
    })
    clearCallOrderConnecting(orderID)
    const calls = useCallStore.getState()
    if (isCallOrderEnded(orderID) || calls.outgoingCall?.order_id !== orderID) {
      await shengwangClient.leaveCall().catch(() => undefined)
      return
    }
    calls.setCurrentCall(connectedCall)
    calls.setOutgoingCall(null)
    usePresenceStore.getState().setCallStatus('busy')
    message.success('User accepted the call')
  } catch (error) {
    clearCallOrderConnecting(orderID)
    reportAnchorActivity({
      event_type: 'outgoing_call_rtc_join_failed',
      event_source: 'anchor_h5_rtc',
      order_id: orderID,
      call_direction: 'anchor_to_user',
      counterparty_user_id: connectedCall?.user_id || readyCall.user_id,
      result: 'failed',
      reason_code: 'rtc_join_failed',
      error_code: 'rtc_join_failed',
      error_message: error instanceof Error ? error.message : 'Failed to connect the call',
      rtc_channel: connectedCall?.channel_name || connectedCall?.rtc_channel || readyCall.channel_name,
      rtc_uid: connectedCall?.uid || readyCall.uid,
      metadata: rtcJoinMetadata(connectedCall || readyCall, 'join_failed', error),
    })
    await shengwangClient.leaveCall().catch(() => undefined)
    if (connectedCall) {
      const calls = useCallStore.getState()
      if (calls.outgoingCall?.order_id === orderID) {
        calls.setOutgoingCall(null)
      }
      usePresenceStore.getState().setCallStatus('idle')
      await releaseEndedOrder(orderID, 'rtc_join_failed')
      message.error(error instanceof Error ? error.message : 'Failed to connect the call')
    } else {
      await releaseOutgoingCall(orderID, CALL_CANCELED_EVENT, 'rtc_join_failed', {
        notify: true,
        warning: error instanceof Error ? error.message : 'Failed to connect the call',
      })
    }
  }
}

async function connectIncomingReadyCall(readyCall: VideoCall) {
  const orderID = readyCall.order_id || ''
  if (!orderID || isCallOrderEnded(orderID)) {
    return
  }
  if (!canAnchorHandleIncomingCall(useAuthStore.getState().anchor)) {
    clearIncomingCallTimeout(orderID)
    const calls = useCallStore.getState()
    if (calls.incomingCall?.order_id === orderID) {
      calls.setIncomingCall(null)
    }
    calls.setIncomingAction(null, null)
    usePresenceStore.getState().setCallStatus('idle')
    await releaseEndedOrder(orderID, 'anchor_account_unavailable')
    return
  }

  let connectedCall: VideoCall | null = null
  try {
    const calls = useCallStore.getState()
    if (calls.currentCall?.order_id === orderID) {
      return
    }
    if (calls.currentCall || calls.outgoingCall) {
      return
    }
    const incomingCall = calls.incomingCall?.order_id === orderID ? calls.incomingCall : null
    connectedCall = {
      ...(incomingCall || null),
      ...readyCall,
      anchor_id: incomingCall?.anchor_id || readyCall.anchor_id,
      user_id: readyCall.user_id || incomingCall?.user_id || '',
      order_id: orderID,
      status: 'accepted',
    } as VideoCall
    if (isCallOrderEnded(orderID)) {
      return
    }
    markCallOrderConnecting(orderID)
    reportAnchorActivity({
      event_type: 'incoming_call_rtc_join_started',
      event_source: 'anchor_h5_rtc',
      order_id: orderID,
      call_direction: 'user_to_anchor',
      counterparty_user_id: connectedCall.user_id,
      rtc_channel: connectedCall.channel_name || connectedCall.rtc_channel,
      rtc_uid: connectedCall.uid,
      metadata: rtcJoinMetadata(connectedCall, 'join_attempt'),
    })
    await shengwangClient.enterCall(connectedCall)
    reportAnchorActivity({
      event_type: 'incoming_call_rtc_joined',
      event_source: 'anchor_h5_rtc',
      order_id: orderID,
      call_direction: 'user_to_anchor',
      counterparty_user_id: connectedCall.user_id,
      rtc_channel: connectedCall.channel_name || connectedCall.rtc_channel,
      rtc_uid: connectedCall.uid,
    })
    clearCallOrderConnecting(orderID)
    const latestCalls = useCallStore.getState()
    if (isCallOrderEnded(orderID) || latestCalls.currentCall || latestCalls.outgoingCall || (latestCalls.incomingCall && latestCalls.incomingCall.order_id !== orderID)) {
      await shengwangClient.leaveCall().catch(() => undefined)
      return
    }
    clearIncomingCallTimeout(orderID)
    latestCalls.setCurrentCall(connectedCall)
    latestCalls.setIncomingCall(null)
    latestCalls.setIncomingAction(null, null)
    usePresenceStore.getState().setCallStatus('busy')
    message.success('Accepted')
  } catch (error) {
    clearCallOrderConnecting(orderID)
    reportAnchorActivity({
      event_type: 'incoming_call_rtc_join_failed',
      event_source: 'anchor_h5_rtc',
      order_id: orderID,
      call_direction: 'user_to_anchor',
      counterparty_user_id: connectedCall?.user_id || readyCall.user_id,
      result: 'failed',
      reason_code: 'rtc_join_failed',
      error_code: 'rtc_join_failed',
      error_message: error instanceof Error ? error.message : 'Failed to connect the call',
      rtc_channel: connectedCall?.channel_name || connectedCall?.rtc_channel || readyCall.channel_name,
      rtc_uid: connectedCall?.uid || readyCall.uid,
      metadata: rtcJoinMetadata(connectedCall || readyCall, 'join_failed', error),
    })
    await shengwangClient.leaveCall().catch(() => undefined)
    const calls = useCallStore.getState()
    if (calls.incomingCall?.order_id === orderID) {
      clearIncomingCallTimeout(orderID)
      calls.setIncomingCall(null)
    }
    calls.setIncomingAction(null, null)
    if (calls.currentCall?.order_id === orderID) {
      calls.setCurrentCall(null)
    }
    usePresenceStore.getState().setCallStatus('idle')
    await releaseEndedOrder(orderID, 'rtc_join_failed')
    message.error(error instanceof Error ? error.message : 'Failed to connect the call')
  }
}

function eventReason(payload: RealtimeMessage, fallback: string) {
  if (typeof payload.reason_code === 'string' && payload.reason_code.trim()) {
    return payload.reason_code.trim()
  }
  return getStringField(asRecord(payload.end), 'reason') || getStringField(asRecord(payload.order), 'reason') || fallback
}

function callTimeoutReason(payload: RealtimeMessage, localDirection?: string) {
  const direction = normalizeClientCallDirection(localDirection) || normalizeClientCallDirection(getDirection(payload))
  return direction === 'user_to_anchor' ? 'anchor_answer_timeout' : 'user_answer_timeout'
}

function callClosedReason(payload: RealtimeMessage) {
  return getStringField(asRecord(payload.close), 'reason') || getStringField(payload, 'reason')
}

function getDurationSeconds(payload: RealtimeMessage) {
  const order = asRecord(payload.order)
  const end = asRecord(payload.end)
  const raw = order?.duration_seconds ?? end?.duration_seconds ?? payload.duration_seconds
  const value = typeof raw === 'string' && raw.trim() ? Number(raw) : raw
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? Math.floor(value) : undefined
}

function reportRealtimeEvent(eventType: string, payload: RealtimeMessage, overrides: Record<string, unknown> = {}) {
  const orderID = getOrderID(payload)
  const { call_direction: callDirectionOverride, ...metadataOverrides } = overrides
  const overrideDirection = typeof callDirectionOverride === 'string' ? normalizeClientCallDirection(callDirectionOverride) : ''
  const direction = overrideDirection || localCallDirectionForOrder(orderID) || endedCallDirectionForOrder(orderID) || getDirection(payload)
  const callDirection = normalizeClientCallDirection(direction)
  const media = asRecord(payload.media)
  const display = asRecord(payload.display)
  reportAnchorActivity({
    event_type: eventType,
    event_source: 'anchor_h5_websocket',
    order_id: orderID || undefined,
    ...(callDirection ? { call_direction: callDirection } : null),
    counterparty_user_id: getStringField(display, 'id') || getUserID(payload) || undefined,
    reason_code: eventReason(payload, callClosedReason(payload) || ''),
    result: 'success',
    rtc_channel: getStringField(media, 'channel_name') || undefined,
    metadata: {
      message_type: getStringField(payload, 'message_type'),
      content_type: getEventType(payload),
      payload_order_id: orderID,
      payload_user_id: getStringField(display, 'id') || getUserID(payload),
      payload_channel_name: getStringField(media, 'channel_name'),
      close_reason: callClosedReason(payload),
      received_at_ms: Date.now(),
      ...metadataOverrides,
    },
  })
}

function normalizeClientCallDirection(direction: unknown): CallDirection | '' {
  if (direction === 'anchor_to_user' || direction === 'user_to_anchor') {
    return direction
  }
  return ''
}

function localCallDirectionForOrder(orderID: string): CallDirection | '' {
  if (!orderID) {
    return ''
  }
  const calls = useCallStore.getState()
  const candidates = [calls.currentCall, calls.outgoingCall, calls.incomingCall]
  for (const call of candidates) {
    const direction = normalizeClientCallDirection(call?.direction)
    if (call?.order_id === orderID && direction) {
      return direction
    }
  }
  return ''
}

const endedCallDirections = new Map<string, CallDirection>()

function rememberCallDirectionForOrder(orderID: string, direction: unknown) {
  const callDirection = normalizeClientCallDirection(direction)
  if (orderID && callDirection) {
    endedCallDirections.set(orderID, callDirection)
  }
}

function markCallOrderEndedWithDirection(orderID: string, direction?: unknown) {
  if (!orderID) {
    return
  }
  rememberCallDirectionForOrder(orderID, direction || localCallDirectionForOrder(orderID) || getLastEndedCallDirection(orderID))
  markCallOrderEnded(orderID)
}

function endedCallDirectionForOrder(orderID: string): CallDirection | '' {
  return normalizeClientCallDirection(orderID ? endedCallDirections.get(orderID) : '')
}

function getLastEndedCallDirection(orderID: string): CallDirection | '' {
  const lastEndedCall = useCallStore.getState().lastEndedCall
  return lastEndedCall?.order_id === orderID ? normalizeClientCallDirection(lastEndedCall.direction) : ''
}

function formatDuration(seconds?: number) {
  if (typeof seconds !== 'number') {
    return ''
  }
  const minutes = Math.floor(seconds / 60)
  const rest = seconds % 60
  return `${minutes}:${String(rest).padStart(2, '0')}`
}

function closeMatchingIncomingCall(orderID: string, presence: ReturnType<typeof usePresenceStore.getState>) {
  const calls = useCallStore.getState()
  if (!calls.incomingCall) {
    return false
  }
  const matchesOrder = Boolean(orderID && calls.incomingCall.order_id === orderID)
  if (!matchesOrder) {
    return false
  }
  clearIncomingCallTimeout(orderID)
  calls.setIncomingCall(null)
  presence.setCallStatus('idle')
  return true
}

function reportPreAcceptIncomingUserCanceled(payload: RealtimeMessage, orderID: string, rawReasonCode: string) {
  const media = asRecord(payload.media)
  const display = asRecord(payload.display)
  reportAnchorActivity({
    event_type: 'incoming_call_canceled',
    event_source: 'anchor_h5_websocket',
    order_id: orderID,
    call_direction: 'user_to_anchor',
    counterparty_user_id: getStringField(display, 'id') || getUserID(payload) || undefined,
    reason_code: 'user_cancel',
    result: 'success',
    from_call_phase: 'ringing_incoming',
    to_call_phase: 'idle',
    rtc_channel: getStringField(media, 'channel_name') || undefined,
    metadata: {
      message_type: getStringField(payload, 'message_type'),
      content_type: getEventType(payload),
      local_call_phase: 'ringing_incoming',
      matched_visible_incoming: true,
      release_skipped: true,
      release_skip_reason: 'pre_accept_incoming',
      payload_order_id: orderID,
      raw_reason_code: rawReasonCode,
      received_at_ms: Date.now(),
    },
  })
}

function closeCompetingIncomingCall(orderID: string, presence: ReturnType<typeof usePresenceStore.getState>) {
  if (!orderID) {
    return false
  }
  const calls = useCallStore.getState()
  const matchesIncoming = Boolean(calls.incomingCall?.order_id === orderID)
  if (!matchesIncoming || calls.currentCall?.order_id === orderID) {
    return false
  }
  clearIncomingCallTimeout(orderID)
  calls.setIncomingCall(null)
  if (calls.incomingActionOrderID === orderID) {
    calls.setIncomingAction(null, null)
  }
  if (!calls.currentCall) {
    presence.setCallStatus('idle')
  }
  return true
}

function clearPendingCallForEndedOrder(orderID: string, presence: ReturnType<typeof usePresenceStore.getState>) {
  if (!orderID) {
    return
  }
  const calls = useCallStore.getState()
  let cleared = false
  if (calls.outgoingCall?.order_id === orderID) {
    clearOutgoingCallTimeout(orderID)
    calls.setOutgoingCall(null)
    cleared = true
  }
  if (calls.incomingCall?.order_id === orderID) {
    clearIncomingCallTimeout(orderID)
    calls.setIncomingCall(null)
    calls.setIncomingAction(null, null)
    cleared = true
  }
  if (cleared && (!calls.currentCall || calls.currentCall.order_id === orderID)) {
    presence.setCallStatus('idle')
  }
}

async function terminateUserToAnchorOrder(orderID: string, reasonCode: string, presence: ReturnType<typeof usePresenceStore.getState>) {
  if (!orderID) {
    return false
  }

  const calls = useCallStore.getState()
  const matchesIncoming = calls.incomingCall?.order_id === orderID
  const matchesIncomingAction = calls.incomingActionOrderID === orderID
  const matchesCurrent = calls.currentCall?.order_id === orderID
  const matchesConnecting = isCallOrderConnecting(orderID)

  if (!matchesIncoming && !matchesIncomingAction && !matchesCurrent && !matchesConnecting) {
    return false
  }

  markCallOrderEndedWithDirection(orderID, 'user_to_anchor')
  clearCallOrderConnecting(orderID)
  clearIncomingCallTimeout(orderID)

  if (matchesIncoming) {
    calls.setIncomingCall(null)
  }
  if (matchesIncomingAction) {
    calls.setIncomingAction(null, null)
  }
  if (matchesCurrent) {
    calls.setCurrentCall(null)
  }

  presence.setCallStatus('idle')
  await shengwangClient.leaveCall().catch(() => undefined)

  if (matchesCurrent || matchesConnecting) {
    void releaseEndedOrder(orderID, reasonCode)
  }

  return true
}

export function clearIncomingCallTimeout(orderID?: string) {
  if (!orderID) {
    return
  }
  clearIncomingAutoAccept(orderID)
  const timer = incomingCallTimeoutTimers.get(orderID)
  if (timer === undefined) {
    return
  }
  globalThis.clearTimeout(timer)
  incomingCallTimeoutTimers.delete(orderID)
}

function clearAllIncomingCallTimeouts() {
  clearAllIncomingAutoAccepts()
  incomingCallTimeoutTimers.forEach((timer) => globalThis.clearTimeout(timer))
  incomingCallTimeoutTimers.clear()
}

export function scheduleIncomingCallTimeout(call: VideoCall) {
  const orderID = call.order_id
  if (!orderID || !call.expire_at || typeof globalThis.setTimeout === 'undefined') {
    return
  }
  clearIncomingCallTimeout(orderID)
  const deadlineMs = call.expire_at * 1000
  const delay = Math.max(0, deadlineMs - Date.now()) + INCOMING_CALL_TIMEOUT_BUFFER_MS
  const timer = globalThis.setTimeout(() => {
    incomingCallTimeoutTimers.delete(orderID)
    if (useCallStore.getState().incomingActionOrderID === orderID) {
      return
    }
    const presence = usePresenceStore.getState()
    if (closeMatchingIncomingCall(orderID, presence)) {
      recordIncomingTimeoutMissed(orderID)
    }
  }, delay)
  incomingCallTimeoutTimers.set(orderID, timer)
}

function recordIncomingTimeoutMissed(orderID: string) {
  if (!orderID) {
    return
  }
  reportAnchorActivity({
    event_type: 'incoming_call_timeout_closed',
    event_source: 'anchor_h5_ui',
    order_id: orderID,
    call_direction: 'user_to_anchor',
    reason_code: 'anchor_answer_timeout',
    from_call_phase: 'ringing_incoming',
    to_call_phase: 'ended',
  })
  void callApi.recordOrderTimeoutMissed(orderID).catch(() => undefined)
}

function recordIncomingCallReceived(call: VideoCall) {
  const orderID = call.order_id
  if (!orderID || recordedIncomingCalls.has(orderID)) {
    return
  }
  recordedIncomingCalls.add(orderID)
  void callApi.recordIncomingOrderReceived(orderID).catch(() => {
    recordedIncomingCalls.delete(orderID)
  })
}

function isAuthError(message: RealtimeMessage) {
  const error = asRecord(message.error)
  const code = Number(error?.code)
  return code === 401 || code === 403
}

class BrowserRealtimeClient implements RealtimeClient {
  private socket: WebSocket | null = null
  private url = ''
  private token = ''
  private commandId = 0
  private connectCommandId = 0
  private reconnectAttempt = 0
  private reconnectTimer: number | null = null
  private intentionalClose = false
  private connected = false
  private handlers = new Map<string, Set<RealtimeEventHandler>>()

  connect({ url, token }: RealtimeConnectOptions) {
    const nextURL = url.trim()
    const nextToken = token.trim()

    if (!nextURL || !nextToken || typeof WebSocket === 'undefined') {
      this.disconnect()
      return
    }

    if (this.socket && this.url === nextURL && this.token === nextToken && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      return
    }

    this.disconnect()
    this.url = nextURL
    this.token = nextToken
    this.intentionalClose = false
    this.openSocket('connecting')
  }

  disconnect() {
    this.intentionalClose = true
    this.clearReconnectTimer()
    clearAllIncomingCallTimeouts()
    this.connected = false
    this.connectCommandId = 0
    this.url = ''
    this.token = ''

    if (this.socket) {
      this.socket.onopen = null
      this.socket.onmessage = null
      this.socket.onerror = null
      this.socket.onclose = null
      this.socket.close()
      this.socket = null
    }

    this.setDisconnectedPresence()
  }

  subscribe<T = unknown>(channel: string, handler: RealtimeEventHandler<T>) {
    const handlers = this.handlers.get(channel) || new Set<RealtimeEventHandler>()
    handlers.add(handler as RealtimeEventHandler)
    this.handlers.set(channel, handlers)

    return () => {
      handlers.delete(handler as RealtimeEventHandler)
      if (handlers.size === 0) {
        this.handlers.delete(channel)
      }
    }
  }

  publish(channel: string, payload: unknown) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN || !this.connected) {
      return
    }

    this.sendLine(buildRealtimeOperationCommand(this.nextCommandId(), channel, payload))
  }

  isConnected() {
    return this.connected && this.socket?.readyState === WebSocket.OPEN
  }

  private openSocket(status: 'connecting' | 'reconnecting') {
    this.clearReconnectTimer()
    const presence = usePresenceStore.getState()
    presence.setConnectionStatus(status)
    presence.setPresence({ online_status: 'offline' })

    try {
      this.socket = new WebSocket(this.url)
    } catch {
      this.scheduleReconnect()
      return
    }

    this.socket.onopen = () => {
      this.sendConnectCommand()
    }

    this.socket.onmessage = (event) => {
      this.handleRawMessage(event.data)
    }

    this.socket.onerror = () => {
      if (!this.intentionalClose) {
        usePresenceStore.getState().setConnectionStatus('reconnecting')
      }
    }

    this.socket.onclose = () => {
      this.connected = false
      this.socket = null

      if (this.intentionalClose) {
        this.setDisconnectedPresence()
        return
      }

      this.scheduleReconnect()
    }
  }

  private sendConnectCommand() {
    this.connectCommandId = this.nextCommandId()
    this.sendLine(buildRealtimeConnectCommand(this.connectCommandId, this.token))
  }

  private nextCommandId() {
    this.commandId += 1
    return this.commandId
  }

  private sendLine(payload: unknown) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return
    }
    this.socket.send(`${JSON.stringify(payload)}\n`)
  }

  private handleRawMessage(raw: unknown) {
    if (raw === '') {
      this.socket?.send('')
      return
    }

    if (typeof raw !== 'string') {
      return
    }

    raw
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .forEach((line) => {
        if (isRealtimeJSONPing(line)) {
          this.socket?.send('{}')
          return
        }
        this.handleMessageLine(line)
      })
  }

  private handleMessageLine(line: string) {
    let message: RealtimeMessage
    try {
      message = JSON.parse(line) as RealtimeMessage
    } catch {
      return
    }

    if (message.id === this.connectCommandId) {
      this.handleConnectReply(message)
      return
    }

    const eventPayload = extractEventPayload(message)
    if (!eventPayload) {
      return
    }

    void this.handleRealtimeEvent(eventPayload).catch(() => undefined)
  }

  private handleConnectReply(message: RealtimeMessage) {
    if (message.error) {
      if (isAuthError(message)) {
        void this.refreshTokenAndReconnect()
        return
      }
      this.connected = false
      this.socket?.close()
      this.setDisconnectedPresence()
      return
    }

    this.connected = true
    this.reconnectAttempt = 0
    const presence = usePresenceStore.getState()
    presence.setConnectionStatus('connected')
    presence.setPresence({
      online_status: 'online',
      call_status: presence.call_status,
    })
    retryPendingEndedOrderReleases()
  }

  async handleRealtimeEvent(eventPayload: RealtimeMessage) {
    eventPayload = unwrapVideoCallPayload(eventPayload)
    const eventType = getEventType(eventPayload)
    if (!eventType) {
      return
    }

    await this.applyKnownEvent(eventType, eventPayload)
    this.emit(eventType, eventPayload)
    this.emit('*', eventPayload)
  }

  private async applyKnownEvent(eventType: string, eventPayload: RealtimeMessage) {
    const presence = usePresenceStore.getState()

    if (eventType === PRESENCE_ACK_EVENT) {
      const onlineStatus = eventPayload.online_status
      const callStatus = eventPayload.call_status
      const nextCallStatus = VALID_CALL_STATUSES.has(callStatus as CallStatus) ? (callStatus as CallStatus) : null
      presence.setPresence({
        ...(VALID_ONLINE_STATUSES.has(onlineStatus as OnlineStatus) ? { online_status: onlineStatus as OnlineStatus } : null),
        ...(nextCallStatus && !(presence.call_status === 'busy' && nextCallStatus !== 'busy') ? { call_status: nextCallStatus } : null),
      })
      return
    }

    if (eventType === 'incoming_call') {
      reportRealtimeEvent('incoming_call_received', eventPayload, {
        call_direction: 'user_to_anchor',
      })
      if (!canAnchorHandleIncomingCall(useAuthStore.getState().anchor)) {
        return
      }
      const incomingCall = toIncomingVideoCall(eventPayload)
      if (!incomingCall) {
        return
      }
      if (isCallOrderEnded(incomingCall.order_id || '')) {
        return
      }
      recordIncomingCallReceived(incomingCall)
      const calls = useCallStore.getState()
      if (calls.currentCall || calls.outgoingCall) {
        return
      }
      if (calls.incomingCall && calls.incomingCall.order_id !== incomingCall.order_id) {
        return
      }
      calls.setIncomingCall(incomingCall)
      void shengwangClient.preloadSDK().catch(() => undefined)
      reportAnchorActivity({
        event_type: 'incoming_call_displayed',
        event_source: 'anchor_h5_ui',
        order_id: incomingCall.order_id,
        call_direction: 'user_to_anchor',
        counterparty_user_id: incomingCall.user_id,
        to_call_phase: 'ringing_incoming',
        rtc_channel: incomingCall.channel_name,
        metadata: {
          display_mode: 'call_ui',
          expire_at: incomingCall.order_expire_at || incomingCall.expire_at,
          received_at_ms: Date.now(),
        },
      })
      scheduleIncomingCallTimeout(incomingCall)
      scheduleIncomingAutoAccept(incomingCall)
      presence.setCallStatus('ringing')
      return
    }

    if (eventType === 'call_rejected' || eventType === CALL_CANCELED_EVENT || eventType === 'call_timeout') {
      const calls = useCallStore.getState()
      const orderID = getOrderID(eventPayload)
      const outgoingOrderID = calls.outgoingCall?.order_id || ''
      const currentOrderID = calls.currentCall?.order_id || ''
      const localOutgoingDirection = calls.outgoingCall?.direction || calls.currentCall?.direction
      const reasonCode = eventReason(eventPayload, eventType === 'call_timeout' ? callTimeoutReason(eventPayload, localOutgoingDirection) : eventType === CALL_CANCELED_EVENT ? 'user_cancel' : 'user_rejected')
      const shouldReleaseConnectedOrder =
        orderID &&
        eventType === CALL_CANCELED_EVENT &&
        reasonCode === 'user_hangup' &&
        (currentOrderID === orderID || isCallOrderConnecting(orderID) || (localOutgoingDirection === 'anchor_to_user' && outgoingOrderID === orderID && calls.outgoingCall?.status === 'accepted'))
      if (shouldReleaseConnectedOrder) {
        markCallOrderEndedWithDirection(orderID, localOutgoingDirection || getDirection(eventPayload))
        clearOutgoingCallTimeout(orderID)
        calls.setOutgoingCall(null)
        void shengwangClient.leaveCall()
        if (calls.incomingCall?.order_id === orderID) {
          clearIncomingCallTimeout(orderID)
          calls.setIncomingCall(null)
          calls.setIncomingAction(null, null)
        }
        if (currentOrderID === orderID) {
          calls.setCurrentCall(null)
        }
        presence.setCallStatus('idle')
        void releaseEndedOrder(orderID, reasonCode)
        return
      }
      if (orderID && localOutgoingDirection === 'anchor_to_user' && (outgoingOrderID === orderID || currentOrderID === orderID)) {
        if (reasonCode === 'user_hangup') {
          markCallOrderEndedWithDirection(orderID, localCallDirectionForOrder(orderID) || getDirection(eventPayload))
        }
        clearOutgoingCallTimeout(orderID)
        calls.setOutgoingCall(null)
        void shengwangClient.leaveCall()
        if (currentOrderID === orderID) {
          calls.setCurrentCall(null)
        }
        presence.setCallStatus('idle')
        if (eventType === 'call_rejected') {
          message.warning('User declined the call')
        }
        void callApi
          .releaseCall(orderID, {
            event_type: eventType,
            reason_code: reasonCode,
          })
          .catch(() => undefined)
        return
      }
      if (orderID && eventType === 'call_rejected' && getDirection(eventPayload) === 'user_to_anchor' && reasonCode === 'user_reject') {
        if (await terminateUserToAnchorOrder(orderID, reasonCode, presence)) {
          message.info('User declined the call')
        }
        return
      }
      if (orderID) {
        markCallOrderEndedWithDirection(orderID, getDirection(eventPayload))
      }
      if (orderID && calls.incomingCall?.order_id === orderID) {
        if (closeMatchingIncomingCall(orderID, presence)) {
          if (eventType === 'call_timeout') {
            recordIncomingTimeoutMissed(orderID)
          }
          if (eventType === CALL_CANCELED_EVENT) {
            reportPreAcceptIncomingUserCanceled(eventPayload, orderID, reasonCode)
          }
        }
        return
      }
      if (getDirection(eventPayload) !== 'user_to_anchor' || (eventType !== CALL_CANCELED_EVENT && eventType !== 'call_timeout')) {
        return
      }
      if (closeMatchingIncomingCall(orderID, presence)) {
        if (eventType === 'call_timeout') {
          recordIncomingTimeoutMissed(orderID)
        }
        if (eventType === CALL_CANCELED_EVENT) {
          reportPreAcceptIncomingUserCanceled(eventPayload, orderID, reasonCode)
        }
      }
      return
    }

    if (eventType === CALL_CLOSED_EVENT) {
      const orderID = getOrderID(eventPayload)
      reportRealtimeEvent('call_closed_received', eventPayload, {
        matched_visible_incoming: Boolean(orderID && useCallStore.getState().incomingCall?.order_id === orderID),
        incoming_action: useCallStore.getState().incomingAction,
      })
      if (callClosedReason(eventPayload) === 'accepted_by_other_anchor' && closeCompetingIncomingCall(orderID, presence)) {
        markCallOrderEndedWithDirection(orderID, getDirection(eventPayload))
        message.info('This call was accepted by another anchor')
      }
      return
    }

    if (eventType === CALL_EXPIRED_EVENT) {
      const orderID = getOrderID(eventPayload)
      if (orderID) {
        markCallOrderEndedWithDirection(orderID, getDirection(eventPayload))
      }
      if (orderID && useCallStore.getState().incomingCall?.order_id === orderID) {
        if (closeMatchingIncomingCall(orderID, presence)) {
          recordIncomingTimeoutMissed(orderID)
        }
        return
      }
      if (getDirection(eventPayload) !== 'user_to_anchor') {
        return
      }
      if (closeMatchingIncomingCall(orderID, presence)) {
        recordIncomingTimeoutMissed(orderID)
      }
      return
    }

    if (eventType === 'call_ready') {
      const direction = getDirection(eventPayload)
      const readyOrderID = getCallReadyOrderID(eventPayload)
      if (isCallOrderEnded(readyOrderID)) {
        return
      }
      const calls = useCallStore.getState()
      const outgoingOrderID = calls.outgoingCall?.order_id || ''
      if (readyOrderID && calls.outgoingCall && outgoingOrderID === readyOrderID) {
        if (calls.outgoingCall.status === 'accepted') {
          return
        }
        const readyCall = toOutgoingReadyCall(eventPayload, readyOrderID)
        if (!readyCall?.order_id) {
          void releaseOutgoingCall(readyOrderID, CALL_CANCELED_EVENT, 'invalid_call_ready')
          return
        }
        clearOutgoingCallTimeout(readyCall.order_id)
        const readyOutgoingCall = {
          ...calls.outgoingCall,
          ...readyCall,
          anchor_id: calls.outgoingCall.anchor_id,
          user_id: readyCall.user_id || calls.outgoingCall.user_id,
          order_id: readyCall.order_id,
          direction: 'anchor_to_user',
          status: 'accepted',
        } as VideoCall
        calls.setOutgoingCall(readyOutgoingCall)
        await connectOutgoingReadyCall(readyOutgoingCall)
        return
      }
      if (readyOrderID && calls.incomingCall?.order_id === readyOrderID) {
        if (!readyOrderID) {
          return
        }
        if (!canAnchorHandleIncomingCall(useAuthStore.getState().anchor)) {
          clearIncomingCallTimeout(readyOrderID)
          calls.setIncomingCall(null)
          calls.setIncomingAction(null, null)
          presence.setCallStatus('idle')
          void releaseEndedOrder(readyOrderID, 'anchor_account_unavailable')
          return
        }
        if (getCallReadyMediaAvailable(eventPayload) === false) {
          message.error('Call accepted, but media credentials are unavailable')
          void releaseEndedOrder(readyOrderID, 'media_unavailable')
          return
        }
        const readyCall = toIncomingReadyCall(eventPayload, readyOrderID)
        if (!readyCall?.order_id) {
          void releaseEndedOrder(readyOrderID, 'invalid_call_ready')
          return
        }
        if (!readyCall.rtc_token) {
          clearIncomingCallTimeout(readyOrderID)
          const calls = useCallStore.getState()
          if (calls.incomingCall?.order_id === readyOrderID) {
            calls.setIncomingCall({
              ...calls.incomingCall,
              ...readyCall,
              status: 'ringing',
            })
          }
          return
        }
        await connectIncomingReadyCall(readyCall)
        return
      }
      if (direction === 'user_to_anchor') {
        if (!readyOrderID) {
          return
        }
        if (!canAnchorHandleIncomingCall(useAuthStore.getState().anchor)) {
          void releaseEndedOrder(readyOrderID, 'anchor_account_unavailable')
          return
        }
        if (getCallReadyMediaAvailable(eventPayload) === false) {
          message.error('Call accepted, but media credentials are unavailable')
          void releaseEndedOrder(readyOrderID, 'media_unavailable')
          return
        }
        const readyCall = toIncomingReadyCall(eventPayload)
        if (!readyCall?.order_id) {
          void releaseEndedOrder(readyOrderID, 'invalid_call_ready')
          return
        }
        if (!readyCall.rtc_token) {
          clearIncomingCallTimeout(readyOrderID)
          const calls = useCallStore.getState()
          if (calls.incomingCall?.order_id === readyOrderID) {
            calls.setIncomingCall({
              ...calls.incomingCall,
              ...readyCall,
              direction: 'user_to_anchor',
              status: 'ringing',
            })
          }
          return
        }
        await connectIncomingReadyCall(readyCall)
        return
      }
      if (direction !== 'anchor_to_user') {
        return
      }
      if (!readyOrderID || !calls.outgoingCall || outgoingOrderID !== readyOrderID) {
        return
      }
      if (calls.outgoingCall.status === 'accepted') {
        return
      }
      const readyCall = toOutgoingReadyCall(eventPayload)
      if (!readyCall?.order_id) {
        void releaseOutgoingCall(readyOrderID, CALL_CANCELED_EVENT, 'invalid_call_ready')
        return
      }
      clearOutgoingCallTimeout(readyCall.order_id)
      const readyOutgoingCall = {
        ...calls.outgoingCall,
        ...readyCall,
        anchor_id: calls.outgoingCall.anchor_id,
        user_id: readyCall.user_id || calls.outgoingCall.user_id,
        order_id: readyCall.order_id,
        status: 'accepted',
      } as VideoCall
      calls.setOutgoingCall(readyOutgoingCall)
      await connectOutgoingReadyCall(readyOutgoingCall)
      return
    }

    if (eventType === 'call_ended') {
      reportRealtimeEvent('call_ended_user_hangup', eventPayload)
      const calls = useCallStore.getState()
      const orderID = getOrderID(eventPayload)
      if (orderID) {
        markCallOrderEndedWithDirection(orderID, calls.currentCall?.direction || getDirection(eventPayload))
        clearPendingCallForEndedOrder(orderID, presence)
      }
      const currentOrderID = calls.currentCall?.order_id || ''
      const direction = calls.currentCall?.direction || getDirection(eventPayload)
      const isUserToAnchorEnd = direction === 'user_to_anchor'
      const isAnchorToUserEnd = direction === 'anchor_to_user'
      if (getStringField(eventPayload, 'message_type') !== VIDEO_CALL_MESSAGE_TYPE || (!isUserToAnchorEnd && !isAnchorToUserEnd) || !orderID || !calls.currentCall || currentOrderID !== orderID) {
        return
      }
      if (endingCallOrders.has(orderID) || calls.endingCallOrderID === orderID) {
        return
      }
      markCallOrderEndedWithDirection(orderID, calls.currentCall?.direction || getDirection(eventPayload))
      endingCallOrders.add(orderID)
      calls.setEndingCallOrderID(orderID)
      const durationSeconds = getDurationSeconds(eventPayload)
      const endedCall = {
        ...calls.currentCall,
        order_id: orderID,
        order_status: getStringField(asRecord(eventPayload.order), 'status') || calls.currentCall.order_status,
        status: 'ended' as const,
        duration_seconds: durationSeconds,
      }
      try {
        await shengwangClient.leaveCall().catch(() => undefined)
        const latest = useCallStore.getState()
        if (latest.currentCall?.order_id !== orderID) {
          return
        }
        clearIncomingCallTimeout(orderID)
        latest.setIncomingCall(null)
        latest.setCurrentCall(null)
        latest.setLastEndedCall(endedCall)
        presence.setCallStatus('idle')
        const durationText = formatDuration(durationSeconds)
        if (isAnchorToUserEnd) {
          message.info(durationText ? `User ended the call. Duration ${durationText}` : 'User ended the call')
        } else {
          message.info(durationText ? `User ended the call. Duration ${durationText}` : 'User ended the call')
        }
        await releaseEndedOrder(orderID, eventReason(eventPayload, 'user_hangup'))
      } finally {
        endingCallOrders.delete(orderID)
        const latest = useCallStore.getState()
        if (latest.endingCallOrderID === orderID) {
          latest.setEndingCallOrderID(null)
        }
      }
      return
    }

    if (eventType === 'call_failed') {
      const calls = useCallStore.getState()
      const orderID = getOrderID(eventPayload)
      if (orderID) {
        markCallOrderEndedWithDirection(orderID, calls.currentCall?.direction || getDirection(eventPayload))
        clearPendingCallForEndedOrder(orderID, presence)
      }
      const currentOrderID = calls.currentCall?.order_id || ''
      if (!calls.currentCall) {
        return
      }
      const matchesCurrentOrder = Boolean(orderID && currentOrderID && currentOrderID === orderID)
      if (!matchesCurrentOrder) {
        return
      }
      markCallOrderEndedWithDirection(orderID, calls.currentCall?.direction || getDirection(eventPayload))
      void shengwangClient.leaveCall()
      clearIncomingCallTimeout(currentOrderID)
      calls.setIncomingCall(null)
      calls.setCurrentCall(null)
      presence.setCallStatus('idle')
      if (currentOrderID) {
        void releaseEndedOrder(currentOrderID, eventReason(eventPayload, 'call_failed'))
      }
    }
  }

  private emit(channel: string, payload: RealtimeMessage) {
    const handlers = this.handlers.get(channel)
    if (!handlers) {
      return
    }
    handlers.forEach((handler) => handler(payload))
  }

  private scheduleReconnect() {
    if (this.intentionalClose || !this.url || !this.token) {
      this.setDisconnectedPresence()
      return
    }

    this.connected = false
    const presence = usePresenceStore.getState()
    presence.setConnectionStatus('reconnecting')
    presence.setPresence({ online_status: 'offline' })

    const delay = RECONNECT_DELAYS_MS[Math.min(this.reconnectAttempt, RECONNECT_DELAYS_MS.length - 1)]
    this.reconnectAttempt += 1
    this.clearReconnectTimer()
    this.reconnectTimer = window.setTimeout(() => {
      this.openSocket('reconnecting')
    }, delay)
  }

  private async refreshTokenAndReconnect() {
    this.connected = false
    const socket = this.socket
    if (socket) {
      socket.onopen = null
      socket.onmessage = null
      socket.onerror = null
      socket.onclose = null
      socket.close()
      this.socket = null
    }

    const nextToken = await useAuthStore.getState().recoverAuthSession()
    if (!nextToken || !this.url || this.intentionalClose) {
      this.setDisconnectedPresence()
      return
    }

    this.token = nextToken
    this.reconnectAttempt = 0
    this.openSocket('reconnecting')
  }

  private clearReconnectTimer() {
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }

  private setDisconnectedPresence() {
    const presence = usePresenceStore.getState()
    presence.setConnectionStatus('disconnected')
    presence.setPresence({ online_status: 'offline' })
  }
}

export const realtimeClient = new BrowserRealtimeClient()

export function createRealtimeClient(): RealtimeClient {
  return new BrowserRealtimeClient()
}

export const __realtimeClientTestHooks = {
  handleEvent: (payload: RealtimeMessage) => realtimeClient.handleRealtimeEvent(payload),
  handleConnectReply: (payload: RealtimeMessage) =>
    (
      realtimeClient as unknown as {
        handleConnectReply: (message: RealtimeMessage) => void
      }
    ).handleConnectReply(payload),
  client: realtimeClient,
  resetEndingCalls: () => endingCallOrders.clear(),
  resetEndedCalls: () => {
    resetEndedCallOrders()
    endedCallDirections.clear()
  },
  resetConnectingCalls: () => resetConnectingCallOrders(),
  resetLifecycleGuard: () => {
    resetCallOrderLifecycleGuard()
    endedCallDirections.clear()
  },
  resetIncomingCallTimeouts: () => clearAllIncomingCallTimeouts(),
}
